import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getRulebookAttachment } from "../rulebookAttachmentClient";
import useCurrentRulebookCard from "./useCurrentRulebookCard";
import useMyRulebooks from "./useMyRulebooks";
import useRulebookAttachGate from "./useRulebookAttachGate";
const NO_CHANGE = { initialRulebookId: null, currentRulebookId: null };
var LookupStatus;
(function (LookupStatus) {
    LookupStatus["Loading"] = "loading";
    LookupStatus["Error"] = "error";
    LookupStatus["Ready"] = "ready";
})(LookupStatus || (LookupStatus = {}));
const startFrom = (draftRulebookId) => ({
    initialId: null,
    baselineId: draftRulebookId ?? null,
    currentId: draftRulebookId ?? null,
});
/**
 * The server attachment arrived. A restored draft keeps its own pick (and baseline), and so does a pick the user made
 * while the lookup was in flight; otherwise the form starts from what is attached.
 */
const withAttached = ({ attachedId, draftRulebookId, isTouched }) => (previous) => {
    const isFromDraft = draftRulebookId !== undefined;
    return {
        initialId: attachedId,
        baselineId: isFromDraft ? previous.baselineId : attachedId,
        currentId: isFromDraft || isTouched ? previous.currentId : attachedId,
    };
};
/**
 * A saved owner's "from" side is unknown until capabilities allow the lookup and it has loaded: saving must not attach
 * or detach anything before. A new owner has nothing attached, so its pick is known as long as it isn't gated off.
 */
const isChoiceKnown = ({ ownerId, isGatedOff, isEnabled, lookup, }) => {
    if (isGatedOff) {
        return false;
    }
    return ownerId ? isEnabled && lookup === LookupStatus.Ready : true;
};
/**
 * A character or persona form's Rulebook choice, mirroring the character form's Lorebook state. Saving the owner
 * applies `change` with `applyRulebookAttachmentChange`. The choice resets whenever the owner or restored draft changes,
 * because the create form is reused across drafts without remounting.
 */
export default function useRulebookAttachmentSelection({ ownerType, ownerId, draftRulebookId }) {
    const { isGatedOff, isEnabled } = useRulebookAttachGate(ownerType);
    const list = useMyRulebooks({ isEnabled });
    const [stateChoice, setStateChoice] = useState(() => startFrom(draftRulebookId));
    const [stateLookup, setStateLookup] = useState(LookupStatus.Ready);
    const [stateLookupAttempt, setStateLookupAttempt] = useState(0);
    const refIsTouched = useRef(false);
    // The choice resets only when the owner or restored draft changes — never on a lookup retry, or a retry
    // would drop an unsaved pick made while the attachment couldn't be read.
    useEffect(() => {
        refIsTouched.current = false;
        setStateChoice(startFrom(draftRulebookId));
    }, [ownerType, ownerId, draftRulebookId]);
    useEffect(() => {
        if (!isEnabled || !ownerId) {
            setStateLookup(LookupStatus.Ready);
            return undefined;
        }
        let isCurrent = true;
        setStateLookup(LookupStatus.Loading);
        getRulebookAttachment({ ownerType, ownerId })
            .then((attachment) => {
            if (isCurrent) {
                setStateChoice(withAttached({ attachedId: attachment?.rulebook_id ?? null, draftRulebookId, isTouched: refIsTouched.current }));
                setStateLookup(LookupStatus.Ready);
            }
        })
            .catch(() => {
            if (isCurrent) {
                setStateLookup(LookupStatus.Error);
            }
        });
        return () => {
            isCurrent = false;
        };
    }, [ownerType, ownerId, draftRulebookId, isEnabled, stateLookupAttempt]);
    const { currentId } = stateChoice;
    const card = useCurrentRulebookCard({ currentId, rulebooks: list.rulebooks, hasListError: list.hasError, isEnabled });
    const { remember } = card;
    /** `rulebook` is the picker's summary of the pick, needed to show a public one that isn't in the user's list. */
    const select = useCallback((rulebookId, rulebook) => {
        refIsTouched.current = true;
        if (rulebook) {
            remember(rulebook);
        }
        setStateChoice((previous) => ({ ...previous, currentId: rulebookId }));
    }, [remember]);
    const clear = useCallback(() => select(null), [select]);
    const { retryCardLookup } = card;
    const retryLookup = useCallback(() => {
        setStateLookupAttempt((attempt) => attempt + 1);
        retryCardLookup();
    }, [retryCardLookup]);
    /** After a successful save the picked Rulebook is what the server has, and the new baseline. */
    const markSaved = useCallback(() => setStateChoice((previous) => ({ ...previous, initialId: previous.currentId, baselineId: previous.currentId })), []);
    /** A local draft stored the pick: it no longer counts as unsaved, while the server attachment (`initialId`) is unchanged. */
    const markDraftSaved = useCallback(() => setStateChoice((previous) => ({ ...previous, baselineId: previous.currentId })), []);
    const isKnown = isChoiceKnown({ ownerId, isGatedOff, isEnabled, lookup: stateLookup });
    const change = useMemo(() => (isKnown ? { initialRulebookId: stateChoice.initialId, currentRulebookId: currentId } : NO_CHANGE), [isKnown, stateChoice.initialId, currentId]);
    return {
        ownerType,
        /** The brand allows attaching Rulebooks to this kind of owner and the user can manage Rulebooks: show the section. */
        isEnabled,
        /**
         * The choice is real: capabilities allow it and, for a saved owner, the server attachment has loaded. Only then may
         * a draft store `currentRulebookId` — before, `null` would read as "removed" and detach the Rulebook on restore.
         */
        isChoiceKnown: isKnown,
        rulebooks: isEnabled ? list.rulebooks : null,
        isListLoading: list.isLoading,
        hasListError: list.hasError,
        reloadRulebooks: list.reload,
        currentRulebookId: isGatedOff ? null : currentId,
        currentRulebook: isEnabled ? card.rulebook : null,
        /** Something is attached (or picked) that can't be found anymore, e.g. it was deleted. */
        isCurrentRulebookMissing: isKnown && card.isMissing,
        isLoading: isEnabled &&
            // A remembered pick (e.g. from Discover) has its card already — a pending list mustn't hide it behind a spinner.
            (stateLookup === LookupStatus.Loading ||
                card.isResolving ||
                (currentId !== null && list.rulebooks === null && list.isLoading && card.rulebook === null)),
        /** The server attachment couldn't be read: the section offers Retry and nothing is saved until it loads. */
        hasLookupError: isEnabled && (stateLookup === LookupStatus.Error || card.hasCardError),
        retryLookup,
        hasChanged: isKnown && currentId !== stateChoice.baselineId,
        change,
        select,
        clear,
        markSaved,
        markDraftSaved,
    };
}
